"use client";

// SheetShareBar — the room's `share ↗ · options ∨`, speaking for what a sheet
// has open (law_sheet_rises, 2026-10-08).
//
// The dashboard keeps one bar in its bottom-left corner. While a sheet is up
// that bar sits under the veil, and what you would want to share is the thing
// in the sheet. So a sheet that says what it is about (`share` on EtherModal)
// carries the SAME bar, in the same type, in the same corner, LIFTED over the
// veil and pointed at its own thing: `share` opens the share studio on it,
// `options` copies or saves its words and carries whatever else the sheet
// offers. On a phone the sheet is the whole width and covers that corner, so
// the bar moves into the sheet's own foot instead.
//
//   seat="room"  the corner, over the veil (hidden on a phone)
//   seat="foot"  the sheet's foot (a phone only)
//
// Text only, never an attribute: a name here may be memory.

import { useRef, useState } from "react";
import type { MenuItem } from "../os/ContextMenu";
import OptionsMenu from "../os/artifact/OptionsMenu";
import type { OsNode } from "../../lib/os/tree";
import dynamic from "next/dynamic";

// the studio is a whole surface (the live page, the desk, the dial): loaded
// only when someone actually shares from a sheet
const ShareStudio = dynamic(() => import("../share/ShareStudio"), { ssr: false });

/** what a sheet is about, for the share bar */
export interface SheetShare {
  /** what the open thing is called (the studio's face, the menu's title) */
  name: string;
  /** the project a door can be minted against (law_share_scope); without it and without `publicUrl`, there is nothing to share, only options */
  projectId?: string | null;
  /** the page's path inside the project the door opens on */
  path?: string | null;
  projectName?: string | null;
  ownerName?: string | null;
  /** a thing that is already public: its url IS the door */
  publicUrl?: string | null;
  /** the open thing's words (markdown), for copy and download */
  text?: string | null;
  /** more rows for options, the sheet's own (open it in its project, show it in the day…) */
  items?: MenuItem[];
  /** what the studio's face renders, when the thing is more than one page of words (a whole project: a folder node) */
  node?: OsNode | null;
}

function Arrow() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M3.5 8.5 8.5 3.5M4.2 3.5h4.3v4.3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function Chevron() {
  return (
    <svg width="12" height="12" viewBox="0 0 13 13" fill="none" aria-hidden="true">
      <path d="M3.25 5.1 6.5 8.35 9.75 5.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
const IcoCopy = (
  <svg width="16" height="16" viewBox="0 0 17 17" fill="none" aria-hidden="true">
    <path d="M5.5 5.5h8v9h-8z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M3.5 11V2.5h8" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
  </svg>
);
const IcoDoc = (
  <svg width="16" height="16" viewBox="0 0 17 17" fill="none" aria-hidden="true">
    <path d="M4 1.5h6l3.5 3.5v10.5H4z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M10 1.5V5h3.5" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
  </svg>
);
const IcoShare = (
  <svg width="16" height="16" viewBox="0 0 17 17" fill="none" aria-hidden="true">
    <path d="M8.5 11V2.5M8.5 2.5 5.5 5.5M8.5 2.5l3 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 8.5v6h9v-6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "innernet";
}

// the Noticer acknowledges the action in its own voice (bottom-right corner)
function notify(msg: string) {
  window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: msg } }));
}

/** the studio renders the shared page from a node; a sheet's thing becomes one page */
function nodeOf(share: SheetShare): OsNode {
  const text = share.text ?? "";
  return {
    key: `sheet:${share.path ?? share.name}`,
    path: share.path ?? "/",
    kind: "dimension",
    name: share.name,
    symbol: "",
    icon: "page",
    summary: text.slice(0, 280),
    stats: [],
    document: text,
    projectId: share.projectId ?? undefined,
  };
}

export default function SheetShareBar({ share, seat }: { share: SheetShare; seat: "room" | "foot" }) {
  const optRef = useRef<HTMLButtonElement | null>(null);
  const [anchor, setAnchor] = useState<{ x: number; y: number; bottom: number } | null>(null);
  const [studio, setStudio] = useState(false);

  const canShare = !!share.projectId || !!share.publicUrl;
  const text = (share.text ?? "").trim();
  const items: MenuItem[] = [
    // on a phone `share` folds into the one menu, as it does in the room's own bar
    ...(seat === "foot" && canShare ? [{ label: "share…", icon: IcoShare, onSelect: () => setStudio(true) } as MenuItem] : []),
    ...(text
      ? ([
          {
            label: "copy the words",
            icon: IcoCopy,
            separated: seat === "foot" && canShare,
            onSelect: () => {
              void navigator.clipboard?.writeText(text);
              notify(`copied ${share.name}`);
            },
          },
          {
            label: "download markdown",
            icon: IcoDoc,
            hint: ".md",
            onSelect: () => {
              const url = URL.createObjectURL(new Blob([text], { type: "text/markdown" }));
              const a = document.createElement("a");
              a.href = url;
              a.download = `${slug(share.name)}.md`;
              a.click();
              URL.revokeObjectURL(url);
              notify(`downloaded ${slug(share.name)}.md`);
            },
          },
        ] as MenuItem[])
      : []),
    ...(share.items ?? []).map((it, i) => (i === 0 && (text || (seat === "foot" && canShare)) ? { ...it, separated: true } : it)),
  ];
  if (!canShare && items.length === 0) return null;

  const toggle = () => {
    const r = optRef.current?.getBoundingClientRect();
    setAnchor((a) => (a ? null : r ? { x: r.left, y: r.top, bottom: r.bottom } : null));
  };

  return (
    <>
      <div className="art-bar eth-sharebar" data-seat={seat} data-noswipe="" data-nodrag="">
        {seat === "room" && canShare ? (
          <button type="button" className="os-link" onClick={() => setStudio(true)}>
            <span className="os-link-text">share</span>
            <Arrow />
          </button>
        ) : null}
        {items.length ? (
          <button ref={optRef} type="button" className="os-link" aria-expanded={!!anchor} onClick={toggle}>
            <span className="os-link-text">options</span>
            <Chevron />
          </button>
        ) : null}
      </div>
      <div data-sheet-layer="" data-open={anchor || studio ? "" : undefined} style={{ display: "contents" }}>
        <OptionsMenu open={!!anchor} onClose={() => setAnchor(null)} items={items} anchor={anchor} title={share.name} />
        {studio ? (
          <ShareStudio
            key={`${share.projectId ?? ""}:${share.path ?? ""}`}
            target={{
              projectId: share.projectId ?? null,
              path: share.path ?? "/",
              name: share.name,
              projectName: share.projectName ?? null,
              ownerName: share.ownerName ?? null,
              publicUrl: share.projectId ? null : share.publicUrl ?? null,
              node: share.node ?? nodeOf(share),
            }}
            onClose={() => setStudio(false)}
          />
        ) : null}
      </div>
    </>
  );
}
